/**
 * 매칭리스트 문자발송 — 발송 모달 / 템플릿 설정 모달 / 미리보기 렌더러
 *
 * MatchList 인스턴스 (parent) 의 state/smsState/데이터를 참조하여 동작.
 * 모달은 #sO (발송) / #mO (템플릿설정) DOM 컨테이너 안에 그려진다.
 *
 * parent 가 제공해야 하는 인터페이스:
 *   state, smsState, internalMembers, internalTemplates, externalTemplates,
 *   PLACEHOLDERS_SHORT, DATA
 *   senderPhones(), currentSenderNum(), externalListFiltered()
 */
import {
    smsTypeOf, byteLength, smsBadge, escapeHtml,
    expandPlaceholders, splitTemplateForHighlight,
} from './MatchListUtils';
import Util from '../../../Common/Util';

export default class SendModal {
    constructor(parent) {
        this.parent = parent;
    }

    /**
     * 발송 모달 렌더 — 대상자 그룹/카운트, 템플릿, 메시지 편집, 미리보기, 발송번호
     * 매 호출마다 #sPC innerHTML 을 재구성하고 bindEvents 로 이벤트 재바인딩.
     */
    render() {
        const p = this.parent;
        const { state, smsState, internalMembers, internalTemplates, externalTemplates, PLACEHOLDERS_SHORT } = p;
        const intNew  = [];
        const intSent = [];
        state.checkedInternal.forEach(no => {
            const m = internalMembers.find(x => x.no === no);
            if (!m) return;
            (m.smsResult ? intSent : intNew).push(m);
        });

        const sentCnt = intSent.length;
        const newCnt  = intNew.length;
        const intCnt  = smsState.excludeSent ? newCnt : newCnt + sentCnt;
        const extCnt  = state.checkedExternal.size;
        const total   = intCnt + extCnt;

        const intTpl = internalTemplates[smsState.intTplIdx] || internalTemplates[0];
        const extTpl = externalTemplates[smsState.extTplIdx] || externalTemplates[0];

        if (!smsState.smsIntText && intCnt > 0 && intTpl) smsState.smsIntText = expandPlaceholders(intTpl.text, PLACEHOLDERS_SHORT);
        if (!smsState.smsExtText && extCnt > 0 && extTpl) smsState.smsExtText = expandPlaceholders(extTpl.text, PLACEHOLDERS_SHORT);

        const intSmsType = smsTypeOf(smsState.smsIntText || '');
        const extSmsType = smsTypeOf(smsState.smsExtText || '');

        let html = '<div style="display:flex"><div style="flex:1;padding:20px;display:flex;flex-direction:column;overflow:hidden">';
        html += '<div style="font-size:14px;font-weight:800;margin-bottom:12px">문자발송</div>';

        // 카드: 내부 신규 / 기발송 / 외부
        html += '<div style="display:flex;gap:6px;margin-bottom:8px">';
        html += `<div style="flex:1;background:#EFF6FF;border-radius:8px;padding:8px;text-align:center">
                    <div style="font-size:14px;color:#3B82F6;font-weight:600">내부 신규</div>
                    <div style="font-size:16px;font-weight:800;color:#1D4ED8">${newCnt}</div>
                 </div>`;
        if (sentCnt > 0) {
            const border = smsState.excludeSent ? '#DC2626' : '#FDE68A';
            const valColor = smsState.excludeSent ? '#DC2626' : '#B45309';
            const strike = smsState.excludeSent ? ' style="text-decoration:line-through"' : '';
            html += `<div style="flex:1;background:#FFF9D9;border-radius:8px;padding:8px;text-align:center;border:1.5px solid ${border}">
                        <div style="font-size:14px;color:#B45309;font-weight:600">기발송</div>
                        <div style="font-size:16px;font-weight:800;color:${valColor}"${strike}>${sentCnt}</div>
                     </div>`;
        }
        html += `<div style="flex:1;background:#F5F3FF;border-radius:8px;padding:8px;text-align:center">
                    <div style="font-size:14px;color:#7C3AED;font-weight:600">외부</div>
                    <div style="font-size:16px;font-weight:800;color:#7C3AED">${extCnt}</div>
                 </div>`;
        html += '</div>';

        // 기발송 토글
        if (sentCnt > 0) {
            const bg = smsState.excludeSent ? '#FFF5F5' : '#FFF9D9';
            const fg = smsState.excludeSent ? '#DC2626' : '#B45309';
            const label = smsState.excludeSent ? `⛔ 기발송 ${sentCnt}명 제외됨` : `⚠ 기발송 ${sentCnt}명 포함`;
            const sw   = smsState.excludeSent ? '제외 중' : '포함 중';
            const knobLeft = smsState.excludeSent ? '16px' : '2px';
            html += `
                <div style="padding:8px 10px;background:${bg};border-radius:6px;margin-bottom:8px;display:flex;align-items:center;justify-content:space-between">
                    <span style="font-size:12px;font-weight:600;color:${fg}">${label}</span>
                    <label id="dupTogLabel" style="display:flex;align-items:center;gap:4px;cursor:pointer;font-size:12px;font-weight:700;color:${fg}">
                        <span>${sw}</span>
                        <div style="width:32px;height:18px;border-radius:9px;background:${fg};position:relative">
                            <div style="width:14px;height:14px;border-radius:50%;background:#fff;position:absolute;top:2px;left:${knobLeft}"></div>
                        </div>
                    </label>
                </div>`;
        }

        // 총 발송 + 대상자 리스트
        html += '<div style="border:1px solid #eee;border-radius:8px;margin-bottom:10px;overflow:hidden">';
        html += `<div style="padding:8px 10px;background:#f8f9fa;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #eee">
                    <span style="font-size:14px;font-weight:700">총 발송 대상</span>
                    <span style="font-size:18px;font-weight:800;color:#79530C">${total}명</span>
                 </div>`;
        html += '<div style="max-height:185px;overflow-y:auto">';

        if (total === 0) {
            html += '<div style="padding:20px;text-align:center;color:#909090;font-size:14px">발송 대상 없음</div>';
        } else {
            const intToSend = internalMembers.filter(m => state.checkedInternal.has(m.no) && !(smsState.excludeSent && m.smsResult));
            const extToSend = p.externalListFiltered().filter(e => state.checkedExternal.has(e.id));
            let rowIdx = 0;

            intToSend.forEach(m => {
                const sent = m.smsResult || '';
                const bg   = rowIdx % 2 ? '#fafafa' : '#fff';
                html += `<div style="display:flex;align-items:center;gap:8px;padding:7px 10px;font-size:12px;border-bottom:1px solid #f1f1f1;background:${bg}">
                            <span style="padding:2px 7px;border-radius:4px;background:#EFF6FF;color:#1D4ED8;font-size:12px;font-weight:600;flex-shrink:0">내부</span>
                            <span style="font-weight:600;min-width:50px">${Util.escapeHtml(m.name)}</span>
                            <span style="color:#626262;font-size:14px">${Util.escapeHtml(m.phone)}</span>`;
                if (sent) {
                    const sBg = sent === '성공' ? '#F0FDF4' : (sent === '실패' ? '#FFF5F5' : '#FFF9D9');
                    const sFg = sent === '성공' ? '#15803D' : (sent === '실패' ? '#DC2626' : '#B45309');
                    html += `<span style="margin-left:auto;padding:2px 6px;border-radius:4px;background:${sBg};color:${sFg};font-size:12px;font-weight:600;flex-shrink:0">발송·${sent}</span>`;
                }
                html += '</div>';
                rowIdx++;
            });

            extToSend.forEach(e => {
                const bg = rowIdx % 2 ? '#fafafa' : '#fff';
                html += `<div style="display:flex;align-items:center;gap:8px;padding:7px 10px;font-size:12px;border-bottom:1px solid #f1f1f1;background:${bg}">
                            <span style="padding:2px 7px;border-radius:4px;background:#F5F3FF;color:#7C3AED;font-size:12px;font-weight:600;flex-shrink:0">외부</span>
                            <span style="font-weight:600;min-width:50px">${Util.escapeHtml(e.company)}</span>
                            <span style="color:#626262;font-size:14px">${Util.escapeHtml(e.phone)}</span>
                         </div>`;
                rowIdx++;
            });
        }
        html += '</div></div>';

        // 템플릿 — 내부
        if (intCnt > 0) {
            if (!smsState.smsIntText && intTpl) smsState.smsIntText = expandPlaceholders(intTpl.text, PLACEHOLDERS_SHORT);
            html += '<div style="margin-bottom:8px"><div style="font-size:12px;font-weight:700;color:#1D4ED8;margin-bottom:6px">내부매칭 템플릿</div>';
            html += '<div style="display:flex;gap:6px;flex-wrap:wrap">';
            if (internalTemplates.length === 0) html += '<span style="color:#909090;font-size:12px">등록된 템플릿 없음</span>';
            internalTemplates.forEach((t, i) => {
                const active = smsState.intTplIdx === i;
                const bd = active ? '#1D4ED8' : '#ccc';
                const bg = active ? '#1D4ED8' : '#fff';
                const fg = active ? '#fff' : '#626262';
                const fw = active ? 700 : 500;
                html += `<button class="spTplBtn" data-type="int" data-idx="${i}" style="padding:5px 14px;border-radius:var(--adm-radius-chip, 999px);font-size:14px;font-family:inherit;cursor:pointer;border:1.5px solid ${bd};background:${bg};color:${fg};font-weight:${fw}">${Util.escapeHtml(t.name)}</button>`;
            });
            html += '</div></div>';
        }

        // 템플릿 — 외부
        if (extCnt > 0) {
            if (!smsState.smsExtText && extTpl) smsState.smsExtText = expandPlaceholders(extTpl.text, PLACEHOLDERS_SHORT);
            html += '<div style="margin-bottom:8px"><div style="font-size:12px;font-weight:700;color:#7C3AED;margin-bottom:6px">외부배차 템플릿</div>';
            html += '<div style="display:flex;gap:6px;flex-wrap:wrap">';
            if (externalTemplates.length === 0) html += '<span style="color:#909090;font-size:12px">등록된 템플릿 없음</span>';
            externalTemplates.forEach((t, i) => {
                const active = smsState.extTplIdx === i;
                const bd = active ? '#7C3AED' : '#ccc';
                const bg = active ? '#7C3AED' : '#fff';
                const fg = active ? '#fff' : '#626262';
                const fw = active ? 700 : 500;
                html += `<button class="spTplBtn" data-type="ext" data-idx="${i}" style="padding:5px 14px;border-radius:var(--adm-radius-chip, 999px);font-size:14px;font-family:inherit;cursor:pointer;border:1.5px solid ${bd};background:${bg};color:${fg};font-weight:${fw}">${Util.escapeHtml(t.name)}</button>`;
            });
            html += '</div></div>';
        }

        // 발송번호 — g5_config 에 등록된 번호 중에서만 선택 (직접 입력 불가)
        const phones = p.senderPhones();
        if (smsState.senderIdx >= phones.length) smsState.senderIdx = 0;
        html += '<div style="padding:8px 0;border-top:1px solid #eee;flex-shrink:0">';
        html += '<div style="display:flex;gap:8px;align-items:center">';
        html += '<span style="font-size:14px;font-weight:700;flex-shrink:0">발송번호</span>';
        html += '<select id="senderSel" style="flex:1;min-width:0;height:36px;border:1px solid #ccc;border-radius:8px;padding:0 10px;font-size:14px;font-family:inherit;color:#15803D;font-weight:600">';
        if (phones.length === 0) {
            html += '<option value="">발송번호를 환경설정에 등록하세요</option>';
        }
        phones.forEach((ph, i) => {
            html += `<option value="${i}"${smsState.senderIdx === i ? ' selected' : ''}>${Util.phoneNumber(ph)}</option>`;
        });
        html += '</select></div>';
        html += '</div></div>';

        // 우측 — textarea ↔ 미리보기 (SMS 전용)
        html += '<div style="width:320px;min-height:500px;background:#f8f9fa;border-left:1px solid #eee;padding:16px;display:flex;flex-direction:column;flex-shrink:0;overflow-y:auto">';

        // textarea 영역
        html += `<div id="spTAArea" style="flex:1;display:${smsState.showPreview ? 'none' : 'flex'};flex-direction:column;gap:8px">`;
        if (intCnt === 0 && extCnt === 0) {
            html += '<div style="flex:1;display:flex;align-items:center;justify-content:center;color:#909090;font-size:14px;font-weight:600">발송 대상 없음</div>';
        }
        const textareaBlock = (kind, txt, color) => {
            const label = `${kind === 'int' ? '내부매칭' : '외부배차'} ${kind === 'int' ? intSmsType : extSmsType} (수정가능)`;
            const byteSpan = `<span id="sms${kind === 'int' ? 'Int' : 'Ext'}Byte">${smsBadge(txt)}</span>`;
            const id = kind === 'int' ? 'smsIntTA' : 'smsExtTA';
            return `
                <div style="flex:1;display:flex;flex-direction:column">
                    <div style="font-size:12px;font-weight:700;color:${color};margin-bottom:4px;display:flex;align-items:center;justify-content:space-between">${label}${byteSpan}</div>
                    <textarea id="${id}" style="width:100%;flex:1;min-height:140px;border:1.5px solid ${color};border-radius:8px;padding:10px;font-size:12px;line-height:1.6;font-family:inherit;resize:none;box-sizing:border-box;background:#fff;color:#121212">${escapeHtml(txt)}</textarea>
                </div>`;
        };

        if (intCnt > 0) html += textareaBlock('int', smsState.smsIntText, '#1D4ED8');
        if (extCnt > 0) html += textareaBlock('ext', smsState.smsExtText, '#7C3AED');
        html += '</div>';

        // 폰 미리보기 영역 (SMS 전용)
        html += `<div id="spPvArea" style="flex:1;display:${smsState.showPreview ? 'flex' : 'none'};flex-direction:column;align-items:center;gap:6px;padding-top:4px">`;
        if (intCnt > 0) {
            html += `<div style="font-size:12px;color:#1D4ED8;font-weight:700;margin-bottom:2px;width:100%">내부매칭 ${intSmsType}</div>`;
            html += this._smsPreview(smsState.smsIntText, p.currentSenderNum());
        }
        if (extCnt > 0) {
            html += `<div style="font-size:12px;color:#7C3AED;font-weight:700;margin:6px 0 2px;width:100%">외부배차 ${extSmsType}</div>`;
            html += this._smsPreview(smsState.smsExtText, p.currentSenderNum());
        }
        html += '</div>';

        // 미리보기 토글 버튼
        const pvBd = smsState.showPreview ? '#1D4ED8' : '#ccc';
        const pvBg = smsState.showPreview ? '#EFF6FF' : '#fff';
        const pvFg = smsState.showPreview ? '#1D4ED8' : '#626262';
        html += `<button id="spPvBtn" style="width:100%;padding:10px;border-radius:8px;border:1.5px solid ${pvBd};background:${pvBg};font-size:12px;font-weight:700;font-family:inherit;cursor:pointer;color:${pvFg};margin-top:8px;flex-shrink:0">${smsState.showPreview ? '✏️ 문자 편집' : '📱 미리보기'}</button>`;

        html += '</div></div>';

        // 하단 — 취소/발송
        html += '<div style="display:flex;gap:8px;padding:12px 20px;border-top:1px solid #eee">';
        html += '<button id="bCancel" style="flex:1;padding:12px;border-radius:10px;border:1px solid #ddd;background:#fff;color:#626262;font-size:14px;font-weight:600">취소</button>';
        if (total > 0) {
            html += `<button id="bCS" style="flex:2;padding:12px;border-radius:10px;border:none;background:#FDCF43;color:#79530C;font-size:14px;font-weight:800">발송 (${total}명)</button>`;
        } else {
            html += '<button style="flex:2;padding:12px;border-radius:10px;border:none;background:#e5e5e5;color:#bbb;font-size:14px;font-weight:800" disabled>발송 대상 없음</button>';
        }
        html += '</div>';

        $('#sPC').html(html);
        this.bindEvents();
    }

    /**
     * 발송 모달 내부 이벤트 — 매 render() 후 호출됨.
     * 버튼/셀렉트/textarea 입력 시 smsState 갱신하고 다시 render().
     */
    bindEvents() {
        const self = this;
        const p = this.parent;
        const { smsState } = p;

        $('#bCancel').on('click', () => $('#sO').hide());

        $('#dupTogLabel').on('click', () => { smsState.excludeSent = !smsState.excludeSent; self.render(); });

        $('.spTplBtn').on('click', function () {
            if ($(this).data('type') === 'int') { smsState.intTplIdx = +$(this).data('idx'); smsState.smsIntText = ''; }
            else                                { smsState.extTplIdx = +$(this).data('idx'); smsState.smsExtText = ''; }
            self.render();
        });

        $('#senderSel').on('change', function () {
            smsState.senderIdx = +$(this).val();
            self.render();
        });

        $('#smsIntTA').on('input', function () {
            smsState.smsIntText = $(this).val();
            $('#smsIntByte').html(smsBadge(smsState.smsIntText));
        });
        $('#smsExtTA').on('input', function () {
            smsState.smsExtText = $(this).val();
            $('#smsExtByte').html(smsBadge(smsState.smsExtText));
        });

        $('#spPvBtn').on('click', () => {
            smsState.showPreview = !smsState.showPreview;
            if (smsState.showPreview) {
                if ($('#smsIntTA').length) smsState.smsIntText = $('#smsIntTA').val();
                if ($('#smsExtTA').length) smsState.smsExtText = $('#smsExtTA').val();
            }
            self.render();
        });

        $('#bCS').on('click', () => {
            // 편집 중 텍스트 캡쳐 (편집 모드일 때만 textarea가 살아 있음)
            if ($('#smsIntTA').length) smsState.smsIntText = $('#smsIntTA').val();
            if ($('#smsExtTA').length) smsState.smsExtText = $('#smsExtTA').val();

            const intToSend = p.internalMembers.filter(m => p.state.checkedInternal.has(m.no) && !(smsState.excludeSent && m.smsResult));
            const extToSend = p.externalListFiltered().filter(e => p.state.checkedExternal.has(e.id));
            if (!intToSend.length && !extToSend.length) { Util.swalert(['발송 대상이 없습니다','warning']); return; }

            // 메시지 본문 검증 — 수신자가 있는 쪽 본문이 비어있으면 차단
            if ((intToSend.length > 0 && !smsState.smsIntText.trim())
             || (extToSend.length > 0 && !smsState.smsExtText.trim())) {
                Util.swalert(['문자내용을 입력해주세요','warning']);
                return;
            }

            const fromPhone = p.currentSenderNum();
            if (!fromPhone) { Util.swalert(['발송번호를 선택하세요','warning']); return; }

            const doSend = () => $.ajax({
                url: '/adm/ajax/order/match/list/send',
                type: 'post',
                data: {
                    num: p.DATA.order?.num || 0,
                    internal: intToSend.map(m => ({ mb_no: m.no, phone: m.phone })),
                    external: extToSend.map(e => ({ ref_num: e.id, phone: e.phone })),
                    int_message: smsState.smsIntText,
                    ext_message: smsState.smsExtText,
                    from_phone:  fromPhone,
                },
                success: function (res) {
                    if (res && res.ok) {
                        $('#sO').hide();
                        smsState.excludeSent = false;
                        // 리로드 후 발송이력 탭으로 진입하도록 표시
                        $j.Common.Store.session.set('matchList.activeTab', 'history');
                        const cnt = intToSend.length + extToSend.length;
                        Util.swalert(
                            ['발송 요청 완료', 'success', cnt + '명에게 문자 발송이 큐에 적재되었습니다.\n발송 결과는 발송이력 탭에서 확인할 수 있습니다.'],
                            function () { location.reload(); }
                        );
                    } else {
                        Util.swalert(['발송 요청 실패', 'error', (res && res.message) || '서버 응답이 올바르지 않습니다']);
                    }
                },
                error: function (xhr) {
                    Util.swalert(['서버 오류', 'error', '발송 요청 중 오류가 발생했습니다 (HTTP ' + (xhr && xhr.status) + ')']);
                },
            });

            // 외부배차후 재발송 확인 — 배차자 1명에게 이미 이 오더로 문자를 보냈으면 한 번 더 확인
            const extSent = p.isExtDispatch ? extToSend.find(e => e.smsResult) : null;
            if (extSent) {
                Util.swalert(
                    ['재발송', 'warning',
                     `${extSent.company || extSent.phone} 님께 이미 이 오더로 문자를 보냈습니다 (발송·${extSent.smsResult}).\n\n다시 발송하시겠습니까?`],
                    doSend,
                    ['다시 발송', '취소']
                );
                return;
            }
            doSend();
        });
    }

    /**
     * 템플릿 설정 모달 — 내부/외부 템플릿 카드 + RCS 미리보기.
     * 현재는 #mO 가 별도로 열리는 경로가 없어 인라인 호출 안 됨 (잠정 보존).
     */
    renderTemplate() {
        const self = this;
        const p = this.parent;
        const { smsState, internalTemplates, externalTemplates, PLACEHOLDERS_SHORT } = p;
        const intTpl = internalTemplates[smsState.intTplIdx];
        const extTpl = externalTemplates[smsState.extTplIdx];

        let html = `
            <div style="padding:20px 24px 0;display:flex;justify-content:space-between">
                <div style="font-size:14px;font-weight:800">문자내용 설정</div>
                <button class="pc" id="meClose">×</button>
            </div>
            <div style="padding:16px 24px;display:flex;gap:24px">
                <div style="flex:1;min-width:300px">`;

        const renderTplCard = (t, i, isInt) => {
            const selected = (isInt ? smsState.intTplIdx : smsState.extTplIdx) === i;
            const color  = isInt ? '#1D4ED8' : '#7C3AED';
            const bg     = selected ? (isInt ? '#EFF6FF' : '#F5F3FF') : '#fff';
            const border = selected ? color : '#eee';
            const buttonLabel = isInt ? t.button : `${t.button} + 문자거절`;
            return `
                <div class="meTplCard" data-type="${isInt ? 'int' : 'ext'}" data-idx="${i}" style="padding:10px 14px;border-radius:8px;border:1.5px solid ${border};background:${bg};margin-bottom:6px;cursor:pointer">
                    <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
                        <span style="font-size:14px;font-weight:700;color:${selected ? color : '#121212'}">${Util.escapeHtml(t.name)}</span>
                        ${selected ? `<span style="padding:1px 6px;border-radius:4px;background:${color};color:#fff;font-size:12px;font-weight:700">적용중</span>` : ''}
                    </div>
                    <div style="font-size:12px;color:#909090;white-space:pre-wrap;line-height:1.5">${Util.escapeHtml(expandPlaceholders(t.text, PLACEHOLDERS_SHORT))}</div>
                    <div style="margin-top:4px;font-size:14px;color:${color};font-weight:600">버튼: ${Util.escapeHtml(buttonLabel)}</div>
                </div>`;
        };

        html += '<div style="margin-bottom:16px"><div style="font-size:12px;font-weight:700;color:#1D4ED8;margin-bottom:8px">내부매칭용 템플릿</div>';
        html += internalTemplates.map((t, i) => renderTplCard(t, i, true)).join('');
        html += '</div>';

        html += '<div><div style="font-size:12px;font-weight:700;color:#7C3AED;margin-bottom:8px">외부배차용 템플릿</div>';
        html += externalTemplates.map((t, i) => renderTplCard(t, i, false)).join('');
        html += '</div></div>';

        // 미리보기
        html += `
            <div style="flex-shrink:0;display:flex;flex-direction:column;align-items:center;gap:8px">
                <div style="font-size:12px;color:#1D4ED8;font-weight:600">내부 미리보기</div>
                ${intTpl ? this._rcsPreview(intTpl.text, intTpl.button, 'int', intTpl.from_phone) : '<div style="color:#909090;font-size:14px">템플릿 없음</div>'}
                <div style="font-size:12px;color:#7C3AED;font-weight:600;margin-top:8px">외부 미리보기</div>
                ${extTpl ? this._rcsPreview(extTpl.text, extTpl.button, 'ext', extTpl.from_phone) : '<div style="color:#909090;font-size:14px">템플릿 없음</div>'}
            </div>
            </div>
            <div style="padding:6px 24px;font-size:14px;color:#626262">내부: 수주하기 버튼 (앱 열림) · 외부: 수주하기 + 문자거절</div>
            <div style="display:flex;gap:8px;padding:12px 24px 20px;justify-content:flex-end">
                <button id="meOk" style="padding:8px 24px;border-radius:8px;border:none;background:#1D4ED8;color:#fff;font-size:12px;font-weight:700">확인</button>
            </div>`;

        $('#mEC').html(html);

        $('#meClose, #meOk').on('click', () => $('#mO').hide());
        $('.meTplCard').on('click', function () {
            if ($(this).data('type') === 'int') smsState.intTplIdx = +$(this).data('idx');
            else                                smsState.extTplIdx = +$(this).data('idx');
            self.renderTemplate();
        });
    }

    // ============================================================
    //  미리보기 (RCS / SMS) — 폰 프레임 안에 메시지 렌더
    // ============================================================
    _rcsPreview(template, button, type, fromPhone) {
        const p = this.parent;
        const parts = splitTemplateForHighlight(template, p.PLACEHOLDERS_SHORT);
        // 치환값(현장주소·발주자명·옵션)은 회원이 쓴 글이다 — 강조 태그만 남기고 글자는 이스케이프
        const body  = parts.map(part => part.bold ? `<strong style="color:#1D4ED8">${Util.escapeHtml(part.text)}</strong>` : Util.escapeHtml(part.text)).join('');
        const len   = expandPlaceholders(template, p.PLACEHOLDERS_SHORT).length;
        const over  = len > 100;
        const counterColor = over ? '#FF382D' : '#909090';
        const sendNum = fromPhone || p.currentSenderNum();

        let actions;
        if (type === 'ext') {
            actions = `
                <div style="margin-top:10px;border-top:1px solid #e5e5e5;padding-top:8px">
                    <div style="background:#FDCF43;border-radius:8px;padding:8px 0;text-align:center;font-size:12px;font-weight:700;color:#79530C;margin-bottom:4px">${Util.escapeHtml(button || '수주하기')}</div>
                    <div style="background:#f1f1f1;border-radius:8px;padding:8px 0;text-align:center;font-size:12px;font-weight:600;color:#909090">문자거절</div>
                </div>`;
        } else {
            actions = `
                <div style="margin-top:10px;border-top:1px solid #e5e5e5;padding-top:8px">
                    <div style="background:#FDCF43;border-radius:8px;padding:8px 0;text-align:center;font-size:12px;font-weight:700;color:#79530C">${Util.escapeHtml(button || '수주하기')}</div>
                </div>`;
        }

        return `
            <div class="rcs" style="width:280px;border-radius:28px;padding:6px 5px">
                <div style="display:flex;justify-content:space-between;padding:3px 14px;font-size:9px;font-weight:600;color:#1c1c1e">
                    <span>오후 3:04</span><span style="font-size:8px">5G</span>
                </div>
                <div style="background:#fff;border-radius:22px;overflow:hidden">
                    <div style="padding:10px 14px;border-bottom:1px solid #f0f0f0;display:flex;align-items:center;gap:8px">
                        <div style="width:34px;height:34px;border-radius:50%;background:#FDCF43;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:13px;color:#79530C;flex-shrink:0">장</div>
                        <div>
                            <div style="font-size:13px;font-weight:700">${Util.phoneNumber(sendNum)}</div>
                        </div>
                    </div>
                    <div style="padding:12px">
                        <div style="background:#f7f7f8;border-radius:14px;padding:12px;font-size:12px;line-height:1.7;white-space:pre-wrap;word-break:break-word">
                            ${body}
                            ${actions}
                        </div>
                    </div>
                </div>
                <div style="padding:0 12px 8px;text-align:right">
                    <span style="font-size:10px;font-weight:600;color:${counterColor}">${len}/100</span>
                </div>
            </div>`;
    }

    _smsPreview(text, fromPhone) {
        const sendNum = fromPhone || this.parent.currentSenderNum();
        const type    = smsTypeOf(text || '');
        const bytes   = byteLength(text || '');
        const iconBg  = type === 'LMS' ? '#DC2626' : '#ccc';

        return `
            <div style="width:280px;background:#e8e8ed;border-radius:28px;padding:6px 5px;box-shadow:inset 0 0 0 2px #d1d1d6,0 4px 20px rgba(0,0,0,.1)">
                <div style="display:flex;justify-content:space-between;padding:3px 14px;font-size:9px;font-weight:600;color:#1c1c1e">
                    <span>오후 3:04</span><span style="font-size:8px">5G</span>
                </div>
                <div style="background:#fff;border-radius:22px;overflow:hidden">
                    <div style="padding:10px 14px;border-bottom:1px solid #f0f0f0;display:flex;align-items:center;gap:8px">
                        <div style="width:34px;height:34px;border-radius:50%;background:${iconBg};display:flex;align-items:center;justify-content:center;font-weight:800;font-size:10px;color:#fff">${type}</div>
                        <div>
                            <div style="font-size:13px;font-weight:700">${Util.phoneNumber(sendNum)}</div>
                            <div style="font-size:9px;color:#909090">${type} · ${bytes}byte</div>
                        </div>
                    </div>
                    <div style="padding:14px">
                        <div style="background:#f7f7f8;border-radius:14px;padding:12px;font-size:12px;line-height:1.7;white-space:pre-wrap;word-break:break-word;color:#333">${escapeHtml(text || '')}</div>
                    </div>
                </div>
            </div>`;
    }
}